import { useEffect, useId, type ReactNode } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import {
  BatteryCharging,
  Box,
  Cctv,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  Fingerprint,
  HardDrive,
  Laptop,
  MapPin,
  Monitor,
  MonitorSmartphone,
  Network,
  Printer,
  Projector,
  RefreshCw,
  ScanBarcode,
  Server,
  Shield,
  Smartphone,
  Tablet,
  Trash2,
  Wifi,
  X,
  type LucideIcon,
} from 'lucide-react';
import { statusHint, statusTone, toneVar } from '@/lib/status';
import { ApiError } from '@/lib/api';
import { num } from '@/lib/format';

// ── Status ───────────────────────────────────────────────────

export function StatusPill({ status, className = '' }: { status: string; className?: string }) {
  const t = toneVar[statusTone(status)];
  return (
    <span
      title={statusHint(status)}
      className={`inline-flex h-6 items-center gap-1.5 rounded-full px-2 text-xs font-medium whitespace-nowrap ${className}`}
      style={{ background: t.soft, color: t.fg }}
    >
      <span aria-hidden className="size-1.5 rounded-full" style={{ background: t.fg }} />
      {status}
    </span>
  );
}

export function StatusDot({ status }: { status: string }) {
  return <span aria-hidden className="inline-block size-2 shrink-0 rounded-full" style={{ background: toneVar[statusTone(status)].fg }} />;
}

// ── Category icon (presentation only; categories come from SQL) ──

const ICONS: [RegExp, LucideIcon][] = [
  [/laptop|notebook/i, Laptop],
  [/desktop/i, MonitorSmartphone],
  [/monitor|display/i, Monitor],
  [/phone|mobile/i, Smartphone],
  [/tablet|ipad/i, Tablet],
  [/scanner|barcode/i, ScanBarcode],
  [/printer/i, Printer],
  [/server/i, Server],
  [/firewall/i, Shield],
  [/switch|router|network/i, Network],
  [/wi-?fi|wireless|access point/i, Wifi],
  [/cctv|camera/i, Cctv],
  [/ups|battery|power/i, BatteryCharging],
  [/hdd|disk|drive|storage/i, HardDrive],
  [/projector/i, Projector],
  [/tracker|gps/i, MapPin],
  [/access control|biometric/i, Fingerprint],
  [/shredder/i, Trash2],
];

export function categoryIcon(type: string): LucideIcon {
  return ICONS.find(([re]) => re.test(type))?.[1] ?? Box;
}

export function CategoryIcon({ type, className = 'size-4' }: { type: string; className?: string }) {
  const Icon = categoryIcon(type);
  return <Icon aria-hidden className={className} strokeWidth={1.75} />;
}

// ── Layout ───────────────────────────────────────────────────

export function PageHeader({ eyebrow, title, description, actions }: { eyebrow?: ReactNode; title: ReactNode; description?: ReactNode; actions?: ReactNode }) {
  return (
    <header className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div className="min-w-0">
        {eyebrow && <p className="eyebrow mb-1.5">{eyebrow}</p>}
        <h1 className="text-[22px] leading-tight font-semibold tracking-[-0.01em] text-fg sm:text-2xl">{title}</h1>
        {description && <p className="mt-1.5 max-w-2xl text-[13px] text-fg-2">{description}</p>}
      </div>
      {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
    </header>
  );
}

export function Panel({
  title,
  description,
  action,
  children,
  className = '',
  bodyClassName = 'p-4 sm:p-5',
  id,
}: {
  title?: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
  id?: string;
}) {
  const headingId = useId();
  return (
    <section id={id} aria-labelledby={title ? headingId : undefined} className={`panel min-w-0 ${className}`}>
      {(title || action) && (
        <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2 border-b border-line px-4 py-3 sm:px-5">
          <div className="min-w-0">
            {title && (
              <h2 id={headingId} className="text-[13px] font-semibold text-fg">
                {title}
              </h2>
            )}
            {description && <p className="mt-0.5 text-xs text-fg-3">{description}</p>}
          </div>
          {action && <div className="max-w-full shrink-0">{action}</div>}
        </div>
      )}
      <div className={bodyClassName}>{children}</div>
    </section>
  );
}

// ── States ───────────────────────────────────────────────────

export function EmptyState({ icon: Icon = Box, title, children, action }: { icon?: LucideIcon; title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center px-6 py-12 text-center">
      <div className="mb-3 grid size-11 place-items-center rounded-full border border-line bg-surface-2 text-fg-3">
        <Icon aria-hidden className="size-5" strokeWidth={1.75} />
      </div>
      <p className="text-sm font-medium text-fg">{title}</p>
      {children && <p className="mt-1 max-w-sm text-[13px] text-fg-2">{children}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function ErrorState({ error, onRetry, compact = false }: { error: unknown; onRetry?: () => void; compact?: boolean }) {
  const e = error instanceof ApiError ? error : null;
  const title =
    e?.status === 403 ? 'You do not have access to this' : e?.status === 404 ? 'Not found' : e?.code === 'network' ? 'Connection problem' : 'This could not be loaded';
  return (
    <div role="alert" className={`flex flex-col items-center text-center ${compact ? 'px-4 py-6' : 'px-6 py-12'}`}>
      <div className="mb-3 grid size-11 place-items-center rounded-full bg-danger-soft text-danger">
        <CircleAlert aria-hidden className="size-5" />
      </div>
      <p className="text-sm font-medium text-fg">{title}</p>
      <p className="mt-1 max-w-md text-[13px] text-fg-2">{e?.message ?? 'Something unexpected happened.'}</p>
      {e?.correlationId && <p className="mono mt-2 text-2xs text-fg-3">Reference {e.correlationId}</p>}
      {onRetry && e?.status !== 403 && e?.status !== 404 && (
        <button className="btn btn-secondary btn-sm mt-4" onClick={onRetry}>
          <RefreshCw aria-hidden className="size-3.5" /> Try again
        </button>
      )}
    </div>
  );
}

export function Skeleton({ className = '' }: { className?: string }) {
  return <div aria-hidden className={`skeleton ${className}`} />;
}

export function LoadingRows({ rows = 8, cols = 5 }: { rows?: number; cols?: number }) {
  return (
    <div role="status" aria-label="Loading" className="divide-y divide-line">
      {Array.from({ length: rows }, (_, r) => (
        <div key={r} className="flex items-center gap-4 px-4 py-3">
          {Array.from({ length: cols }, (_, c) => (
            <Skeleton key={c} className={`h-3.5 ${c === 0 ? 'w-20' : c === 1 ? 'flex-1' : 'w-24'}`} />
          ))}
        </div>
      ))}
    </div>
  );
}

// ── Data display ─────────────────────────────────────────────

/** Initials on a soft gradient whose hue is stable per name, so people are easy to tell apart at a glance. */
export function Avatar({ name, size = 32 }: { name: string; size?: number }) {
  const parts = name.split(/\s+/).filter(Boolean);
  const text = parts.slice(0, 2).map((p) => p[0]!.toUpperCase()).join('');
  let h = 0;
  for (const ch of name) h = (h * 31 + ch.charCodeAt(0)) % 360;
  return (
    <span
      aria-hidden
      className="avatar grid shrink-0 place-items-center rounded-full font-semibold"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.36), ['--h' as string]: h }}
    >
      {text || '?'}
    </span>
  );
}

export function KeyValue({ label, children, mono = false }: { label: string; children: ReactNode; mono?: boolean }) {
  const empty = children === null || children === undefined || children === '';
  return (
    <div className="min-w-0">
      <dt className="text-xs text-fg-3">{label}</dt>
      <dd className={`mt-0.5 truncate text-[13px] ${empty ? 'text-fg-3' : 'text-fg'} ${mono && !empty ? 'mono' : ''}`} title={typeof children === 'string' ? children : undefined}>
        {empty ? 'Not recorded' : children}
      </dd>
    </div>
  );
}

/**
 * A page past the end (an old link, or a list that shrank) would show an empty "nothing here" state with no
 * pagination to step back: move to the last page that has rows instead.
 */
export function useClampPage(data: { items: unknown[]; total: number } | undefined, page: number, pageSize: number, onPage: (p: number) => void) {
  const last = data ? Math.max(1, Math.ceil(data.total / pageSize)) : 1;
  const beyond = !!data && data.items.length === 0 && data.total > 0 && page > last;
  useEffect(() => {
    if (beyond) onPage(last);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [beyond, last]);
}

export function Pagination({ page, pageSize, total, onPage }: { page: number; pageSize: number; total: number; onPage: (p: number) => void }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);
  return (
    <nav aria-label="Pagination" className="flex items-center justify-between gap-3 border-t border-line px-4 py-2.5 text-xs text-fg-2">
      <span className="tnum">
        {num(from)}–{num(to)} of {num(total)}
      </span>
      <div className="flex items-center gap-1">
        <button className="btn btn-ghost btn-sm btn-icon" aria-label="Previous page" disabled={page <= 1} onClick={() => onPage(page - 1)}>
          <ChevronLeft aria-hidden className="size-4" />
        </button>
        <span className="tnum px-1.5">
          Page {page} of {pages}
        </span>
        <button className="btn btn-ghost btn-sm btn-icon" aria-label="Next page" disabled={page >= pages} onClick={() => onPage(page + 1)}>
          <ChevronRight aria-hidden className="size-4" />
        </button>
      </div>
    </nav>
  );
}

// ── Forms ────────────────────────────────────────────────────

export function Field({ label, error, hint, children, htmlFor, required }: { label: string; error?: string; hint?: string; children: ReactNode; htmlFor: string; required?: boolean }) {
  return (
    <div>
      <label className="label" htmlFor={htmlFor}>
        {label}
        {required && <span className="text-danger"> *</span>}
      </label>
      {children}
      {error ? (
        <p id={`${htmlFor}-error`} className="mt-1 text-xs text-danger">
          {error}
        </p>
      ) : hint ? (
        <p className="mt-1 text-xs text-fg-3">{hint}</p>
      ) : null}
    </div>
  );
}

/** camelCase field key to words: returnCondition → Return condition. */
const fieldLabel = (k: string) => (k.charAt(0).toUpperCase() + k.slice(1).replace(/([A-Z])/g, ' $1').toLowerCase()).replace(/^(.)/, (c) => c.toUpperCase());

/**
 * The request error, plus the API's per-field messages when the form does not show them beside its inputs
 * (otherwise "Some fields need attention." would not say which).
 */
export function FormError({ error, fieldsInline = false }: { error: unknown; fieldsInline?: boolean }) {
  if (!error) return null;
  const e = error instanceof ApiError ? error : null;
  const fields = !fieldsInline && e?.fields ? Object.entries(e.fields) : [];
  return (
    <div role="alert" className="flex gap-2 rounded-[var(--radius-md)] bg-danger-soft px-3 py-2.5 text-[13px] text-danger">
      <CircleAlert aria-hidden className="mt-0.5 size-4 shrink-0" />
      <div>
        <p>{e?.message ?? 'Something went wrong.'}</p>
        {fields.length > 0 && (
          <ul className="mt-1 list-disc space-y-0.5 pl-4 text-xs">
            {fields.map(([k, msg]) => (
              <li key={k}>
                {fieldLabel(k)}: {msg}
              </li>
            ))}
          </ul>
        )}
        {e?.correlationId && e.status >= 500 && <p className="mono mt-1 text-2xs opacity-80">Reference {e.correlationId}</p>}
      </div>
    </div>
  );
}

// ── Dialog ───────────────────────────────────────────────────

export function Modal({
  open,
  onOpenChange,
  title,
  description,
  children,
  width = 'max-w-lg',
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  title: string;
  description?: ReactNode;
  children: ReactNode;
  width?: string;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="animate-fade modal-overlay fixed inset-0 z-50" />
        <Dialog.Content
          className={`modal-pop fixed top-1/2 left-1/2 z-50 max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] ${width} -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-[16px] border border-line bg-surface shadow-[var(--shadow-pop)] focus:outline-none`}
        >
          <span aria-hidden className="pointer-events-none absolute inset-x-6 top-0 z-20 h-px bg-[linear-gradient(90deg,transparent,var(--brand),transparent)] opacity-70" />
          <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-line bg-surface/95 px-5 py-4 backdrop-blur-sm">
            <div>
              <Dialog.Title className="text-[15px] font-semibold text-fg">{title}</Dialog.Title>
              {description ? (
                <Dialog.Description className="mt-1 text-[13px] text-fg-2">{description}</Dialog.Description>
              ) : (
                <Dialog.Description className="sr-only">{title}</Dialog.Description>
              )}
            </div>
            <Dialog.Close className="btn btn-ghost btn-sm btn-icon -mt-1 -mr-2" aria-label="Close">
              <X aria-hidden className="size-4" />
            </Dialog.Close>
          </div>
          <div className="px-5 py-4">{children}</div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
